import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import ReactQuill, { Quill } from "react-quill";
import { get, uri } from "helpers/api_helper";
import * as api from "../../../constants/api_constants";
import { formatToPascal } from "helpers/utils/nameUtils";
import DragAndDropComponent from "components/FileUploadDrag/DragAndDrop";

function ViewEmployee() {
  const navigate = useNavigate();
  const { empId } = useParams();
  
  const [fetchedData, setFetchedData] = useState({});
  const [employees, setEmployees] = useState([]);

  const [uploadedDocs, setUploadedDocs] = useState({
    resume: {},
    educationalCertificates: {},
    nationalIdentification: {},
    cancelledCheckbook: {},
  });

  const [companyFiles, setCompanyFiles] = useState([
    {
      experienceLetter: {},
      relieving: {},
    },
  ]);

  useEffect(() => {
    if (empId) {
      fetchEmployeesData(empId);
    }
  }, [empId]);

  const fetchEmployeesData = async (empId) => {
    try {
      const response = await get(api.GET_EMPLOYEE_BY_ID(empId));
      const empData = response?.data;
      console.log("API Response:", response);

      // Fetch all employees for reportingTo mapping
      const res = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
      const employeeResponse = res.map((item) => ({
        value: String(item.employeeId),
        label: `${item.firstName} ${item.lastName || ""}`.trim(),
      }));
      console.log("Employee Response:", employeeResponse);
      setEmployees(employeeResponse);

      // Find reporting manager
      const reportingManager = employeeResponse.find(
        (item) => item.value === String(empData?.reportingTo)
      );
      console.log("Reporting Manager:", reportingManager);

      const employeeDoc = empData?.documents?.[0] || {};
      const fileIds = [
        { key: "educationalCertificates", id: employeeDoc.educationalCertificates },
        { key: "resume", id: employeeDoc.resume },
        { key: "nationalIdentification", id: employeeDoc.nationalIdentification },
        { key: "cancelledCheckbook", id: employeeDoc.cancelledCheque },
        { key: "experienceLetter", id: employeeDoc.experienceLetter },
        { key: "relieving", id: employeeDoc.relievingLetter },
      ].filter((item) => item.id);

      // Add payslip IDs to fileIds
      const payslipIds =
        employeeDoc.payslips?.map((id, index) => ({
          key: `paySlips.month${index + 1}`,
          id,
        })) || [];
      fileIds.push(...payslipIds);

      // Fetch file details for all IDs
      const fileDetails = await Promise.all(
        fileIds.map(async ({ key, id }) => {
          try {
            const fileResponse = await get(
              `${uri}/fileservice/api/files/view/${id}`
            );
            return {
              key,
              file: {
                id: fileResponse.id,
                fileName: fileResponse.fileName,
                name: fileResponse.fileName,
                path: fileResponse.path || `/Uploads/${fileResponse.fileName}`,
              },
            };
          } catch (error) {
            console.log(`Error fetching file ${id}:`, error);
            return { key, file: null };
          }
        })
      );

      // Filter out null files and organize into uploadedDocs and companyFiles
      const newUploadedDocs = { ...uploadedDocs };
      const newCompanyFiles = [
        {
          ...companyFiles[0],
          paySlips: { month1: {}, month2: {}, month3: {} },
        },
      ];

      fileDetails.forEach(({ key, file }) => {
        if (!file) return;

        if (
          [
            "resume",
            "cancelledCheckbook",
            "nationalIdentification",
            "educationalCertificates",
          ].includes(key)
        ) {
          newUploadedDocs[key] = file;
        }

        if (["experienceLetter", "relieving"].includes(key)) {
          newCompanyFiles[0][key] = file;
        }

        if (key.startsWith("paySlips.month")) {
          const month = key.split(".")[1];
          newCompanyFiles[0].paySlips[month] = file;
        }
      });

      setUploadedDocs(newUploadedDocs);
      console.log("Uploaded Docs:", newUploadedDocs);
      setCompanyFiles(newCompanyFiles);
      console.log("Company Files:", newCompanyFiles);

      // Convert status to string to match select option values
      const statusValue = empData?.status !== undefined ? String(empData.status) : "";
      console.log("Status Value:", statusValue);

      setFetchedData({
        employeeId: empData.employeeId,
        employeeFirstName: empData?.firstName || "",
        employeeMiddleName: empData?.middleName || "",
        employeeLastName: empData?.lastName || "",
        employeeCodeId: empData?.employeeCode || "",
        employeeEmail: empData?.email || "",
        employeeMobile: empData?.mobile || "",
        employeeDesignation: formatToPascal(empData?.designation) || "",
        employeeRole: empData?.role || "",
        exitDate: empData?.employeeExitDate?.slice(0, 10) || "",
        exitReason: empData?.exitReason || "",
        identificationNumber: empData?.identificationNumber || "",
        identificationType: formatToPascal(empData?.identificationType) || "",
        employmentType: formatToPascal(empData?.employmentType) || "",
        hireDate: empData?.hireDate?.slice(0, 10) || "",
        department: formatToPascal(empData?.department) || "",
        reportingTo: reportingManager?.label || "",
        source: empData?.source || "",
        probationEndDate: empData?.probationEndDate?.slice(0, 10) || "",
        noticePeriod: empData?.noticePeriod || "",
        status: statusValue, // Ensure status is a string ("0" or "1")
        personalInformationId: empData?.personalInformation[0]?.id || "",
        dateOfBirth: empData?.personalInformation?.[0]?.dateOfBirth?.slice(0, 10) || "",
        gender: formatToPascal(empData?.personalInformation?.[0]?.gender) || "",
        maritalStatus: formatToPascal(empData?.personalInformation?.[0]?.maritalStatus) || "",
        bloodGroup: formatToPascal(empData?.personalInformation?.[0]?.bloodGroup) || "",
        residenceId: empData?.residence[0]?.id,
        country: empData?.residence?.[0]?.permanentAddress?.country || "",
        zipCode: empData?.residence?.[0]?.permanentAddress?.zipCode || "",
        province: empData?.residence?.[0]?.permanentAddress?.province || "",
        city: empData?.residence?.[0]?.permanentAddress?.city || "",
        address: empData?.residence?.[0]?.permanentAddress?.address || "",
        CAcountry: empData?.residence?.[0]?.communicationAddress?.country || "",
        CAzipCode: empData?.residence?.[0]?.communicationAddress?.zipCode || "",
        CAprovince: empData?.residence?.[0]?.communicationAddress?.province || "",
        CAcity: empData?.residence?.[0]?.communicationAddress?.city || "",
        CAaddress: empData?.residence?.[0]?.communicationAddress?.address || "",
        emergencyContactsId: empData?.emergencyContacts?.[0]?.id || "",
        contactPersonName: empData?.emergencyContacts?.[0]?.contactPersonName || "",
        contactMobile: empData?.emergencyContacts?.[0]?.mobile || "",
        relation: empData?.emergencyContacts?.[0]?.relation || "",
        socialProfileId: empData?.socialProfile?.[0]?.id || "",
        linkedIn: empData?.socialProfile?.[0]?.linkedIn || "",
        facebook: empData?.socialProfile?.[0]?.facebook || "",
        twitter: empData?.socialProfile?.[0]?.twitter || "",
        rolesId: empData?.rolesAndResponsibilities?.[0]?.id || "",
        roles: empData?.rolesAndResponsibilities?.[0].wordTypeBox || "",
      });
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: empId
      ? fetchedData
      : {
          employeeFirstName: "",
          employeeMiddleName: "",
          employeeLastName: "",
          employeeCodeId: "",
          employeeEmail: "",
          employeeMobile: "",
          employeeDesignation: "",
          employeeRole: "",
          identificationNumber: "",
          identificationType: "",
          employmentType: "",
          hireDate: "",
          department: "",
          reportingTo: "",
          source: "",
          probationEndDate: "",
          noticePeriod: "",
          status: "",
          exitDate: "",
          exitReason: "",
          dateOfBirth: "",
          gender: "",
          maritalStatus: "",
          bloodGroup: "",
          country: "",
          zipCode: "",
          province: "",
          city: "",
          address: "",
          CAcountry: "",
          CAzipCode: "",
          CAprovince: "",
          CAcity: "",
          CAaddress: "",
          contactPersonName: "",
          contactMobile: "",
          relation: "",
          documentCategory: "EDUCATION",
          documentName: "BSc Computer Science",
          filePath: "/documents/bsc-cs.pdf",
          fileType: "PDF",
          fileSize: 50000,
          linkedIn: "",
          facebook: "",
          twitter: "",
          roles: "",
        },
    validationSchema: Yup.object({
      exitDate: Yup.date().when("status", {
        is: (status) => status === "0",
        then: (schema) =>
          schema
            .required("Please enter exit date")
            .test("is-after-hire-date", "Exit date must be after hire date", function (value) {
              const { hireDate } = this.parent;
              return !value || !hireDate || new Date(value) > new Date(hireDate);
            }),
        otherwise: (schema) => schema.nullable(),
      }),
      exitReason: Yup.string().when("status", {
        is: (status) => status === "0",
        then: (schema) => schema.required("Please enter exit reason"),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
  });

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading="View Employee"
          routePath="Home"
          pageName="View Employee"
        />
        <Row className="px-4 mx-0 my-3">
          <Col className="p-0 m-0" xs={12} md={12}>
            <Card>
              <CardBody>
                <Form
                  className="row g-3 needs-validation py-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                >
                  {/* Employee Information */}
                  <Row className="card-group">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Employee Information</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeFirstName">First Name</Label>
                          <Input
                            name="employeeFirstName"
                            type="text"
                            className="form-control"
                            id="employeeFirstName"
                            value={validation.values.employeeFirstName || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeMiddleName">Middle Name</Label>
                          <Input
                            name="employeeMiddleName"
                            type="text"
                            className="form-control"
                            id="employeeMiddleName"
                            value={validation.values.employeeMiddleName || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeLastName">Last Name</Label>
                          <Input
                            name="employeeLastName"
                            type="text"
                            className="form-control"
                            id="employeeLastName"
                            value={validation.values.employeeLastName || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeCodeId">Employee Id</Label>
                          <Input
                            name="employeeCodeId"
                            type="text"
                            className="form-control"
                            id="employeeCodeId"
                            value={validation.values.employeeCodeId || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeEmail">Email</Label>
                          <Input
                            name="employeeEmail"
                            type="text"
                            className="form-control"
                            id="employeeEmail"
                            value={validation.values.employeeEmail || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeMobile">Mobile</Label>
                          <Input
                            name="employeeMobile"
                            type="text"
                            className="form-control"
                            id="employeeMobile"
                            value={validation.values.employeeMobile || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="identificationType">Identification Type</Label>
                          <Input
                            type="text"
                            name="identificationType"
                            className="form-control"
                            id="identificationType"
                            value={validation.values.identificationType || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="identificationNumber">Identification Number</Label>
                          <Input
                            name="identificationNumber"
                            type="text"
                            className="form-control"
                            id="identificationNumber"
                            value={validation.values.identificationNumber || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="department">Department</Label>
                          <Input
                            type="text"
                            name="department"
                            className="form-control"
                            id="department"
                            value={validation.values.department || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeDesignation">Designation</Label>
                          <Input
                            type="text"
                            name="employeeDesignation"
                            className="form-control"
                            id="employeeDesignation"
                            value={validation.values.employeeDesignation || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employmentType">Employment Type</Label>
                          <Input
                            type="text"
                            name="employmentType"
                            className="form-control"
                            id="employmentType"
                            value={validation.values.employmentType || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="reportingTo">Reporting To</Label>
                          <Input
                            type="text"
                            name="reportingTo"
                            className="form-control"
                            id="reportingTo"
                            value={validation.values.reportingTo || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="source">Source</Label>
                          <Input
                            type="text"
                            name="source"
                            className="form-control"
                            id="source"
                            value={validation.values.source || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="hireDate">Hire Date</Label>
                          <Input
                            name="hireDate"
                            type="date"
                            className="form-control"
                            id="hireDate"
                            value={validation.values.hireDate || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="probationEndDate">Probation End Date</Label>
                          <Input
                            name="probationEndDate"
                            type="date"
                            className="form-control"
                            id="probationEndDate"
                            value={validation.values.probationEndDate || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="noticePeriod">Notice Period</Label>
                          <Input
                            name="noticePeriod"
                            type="text"
                            className="form-control"
                            id="noticePeriod"
                            value={validation.values.noticePeriod || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="exitDate">Exit Date</Label>
                          <Input
                            name="exitDate"
                            type="date"
                            className="form-control"
                            id="exitDate"
                            value={validation.values.exitDate || ""}
                            invalid={validation.touched.exitDate && validation.errors.exitDate}
                            disabled
                          />
                          {validation.touched.exitDate && validation.errors.exitDate ? (
                            <FormFeedback type="invalid">{validation.errors.exitDate}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="exitReason">Exit Reason</Label>
                          <Input
                            name="exitReason"
                            type="text"
                            className="form-control"
                            id="exitReason"
                            value={validation.values.exitReason || ""}
                            invalid={validation.touched.exitReason && validation.errors.exitReason}
                            disabled
                          />
                          {validation.touched.exitReason && validation.errors.exitReason ? (
                            <FormFeedback type="invalid">{validation.errors.exitReason}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="status">Status</Label>
                          <Input
                            type="select"
                            name="status"
                            className="form-select"
                            id="status"
                            value={validation.values.status || ""}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.status && validation.errors.status}
                            disabled
                          >
                            <option value="">Select</option>
                            <option value="1">Active</option>
                            <option value="0">In-Active</option>
                          </Input>
                          {validation.touched.status && validation.errors.status ? (
                            <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  {/* Personal Information */}
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Personal Information</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="dateOfBirth">Date of Birth</Label>
                          <Input
                            name="dateOfBirth"
                            type="date"
                            className="form-control"
                            id="dateOfBirth"
                            value={validation.values.dateOfBirth || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="gender">Gender</Label>
                          <Input
                            type="text"
                            name="gender"
                            className="form-control"
                            id="gender"
                            value={validation.values.gender || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="maritalStatus">Marital Status</Label>
                          <Input
                            type="text"
                            name="maritalStatus"
                            className="form-control"
                            id="maritalStatus"
                            value={validation.values.maritalStatus || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="bloodGroup">Blood Group</Label>
                          <Input
                            type="text"
                            name="bloodGroup"
                            className="form-control"
                            id="bloodGroup"
                            value={validation.values.bloodGroup || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  {/* Employee Permanent Residence */}
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Employee Permanent Residence</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="country">Country</Label>
                          <Input
                            type="text"
                            name="country"
                            className="form-control"
                            id="country"
                            value={validation.values.country || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="zipCode">Zip Code</Label>
                          <Input
                            name="zipCode"
                            type="text"
                            className="form-control"
                            id="zipCode"
                            value={validation.values.zipCode || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="province">Province</Label>
                          <Input
                            type="text"
                            name="province"
                            className="form-control"
                            id="province"
                            value={validation.values.province || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="city">City</Label>
                          <Input
                            name="city"
                            type="text"
                            className="form-control"
                            id="city"
                            value={validation.values.city || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="address">Address</Label>
                          <Input
                            name="address"
                            type="textarea"
                            className="form-control"
                            id="address"
                            value={validation.values.address || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  {/* Employee Communication Address */}
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Employee Communication Address</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAcountry">Country</Label>
                          <Input
                            type="text"
                            name="CAcountry"
                            className="form-control"
                            id="CAcountry"
                            value={validation.values.CAcountry || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAzipCode">Zip Code</Label>
                          <Input
                            name="CAzipCode"
                            type="text"
                            className="form-control"
                            id="CAzipCode"
                            value={validation.values.CAzipCode || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAprovince">Province</Label>
                          <Input
                            type="text"
                            name="CAprovince"
                            className="form-control"
                            id="CAprovince"
                            value={validation.values.CAprovince || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAcity">City</Label>
                          <Input
                            name="CAcity"
                            type="text"
                            className="form-control"
                            id="CAcity"
                            value={validation.values.CAcity || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAaddress">Address</Label>
                          <Input
                            name="CAaddress"
                            type="textarea"
                            className="form-control"
                            id="CAaddress"
                            value={validation.values.CAaddress || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  {/* Emergency Contacts */}
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Emergency Contacts</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactPersonName">Contact Person Name</Label>
                          <Input
                            name="contactPersonName"
                            type="text"
                            className="form-control"
                            id="contactPersonName"
                            value={validation.values.contactPersonName || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactMobile">Mobile</Label>
                          <Input
                            name="contactMobile"
                            type="text"
                            className="form-control"
                            id="contactMobile"
                            value={validation.values.contactMobile || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="relation">Relation</Label>
                          <Input
                            type="text"
                            name="relation"
                            className="form-control"
                            id="relation"
                            value={validation.values.relation || ""}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  {/* Documents */}
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Documents</p>
                    </Col>
                    <Row className="py-3">
                      <Col md="3">
                        <Label htmlFor="relieving">Relieving Letter</Label>
                        <DragAndDropComponent
                          initialFile={companyFiles[0]?.relieving}
                          readOnly={true}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="experienceLetter">Experience Letter</Label>
                        <DragAndDropComponent
                          initialFile={companyFiles[0]?.experienceLetter}
                          readOnly={true}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="resume">Resume</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.resume}
                          readOnly={true}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="educationalCertificates">Educational Documents</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.educationalCertificates}
                          readOnly={true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md="3">
                        <Label htmlFor="cancelledCheckbook">Cancelled Checkbook</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.cancelledCheckbook}
                          readOnly={true}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="nationalIdentification">National Identification</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.nationalIdentification}
                          readOnly={true}
                        />
                      </Col>
                    </Row>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Footer
        saveText="Edit Employee"
        save={() => navigate(`/add-employee/${empId}`)}
        showBackConfirmation={false}
      />
    </div>
  );
}

export default ViewEmployee;